import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../app/store';
import { getSubscriptionStatus } from '../app/ActionTypes/subscriptionActionTypes';
import { activateTrialSubscription } from '../services/subscription';
import { Notify } from '../services/notificationService';
import { trackEvent } from '../utils/monitoring';

export interface TrialActivationState {
  showConfirmation: boolean;
  priceId: string | null;
  confirming: boolean;
}

/**
 * Hook to handle the redirect from Stripe portal after adding a payment method.
 * When the user completes the payment_method_update flow, Stripe redirects back
 * with ?trial_activated=true. This hook detects that and returns state for showing
 * a confirmation modal. The actual activation happens when confirmActivation() is called.
 */
export function useTrialActivationHandler(): {
  activationState: TrialActivationState;
  confirmActivation: () => Promise<void>;
  cancelActivation: () => void;
} {
  const dispatch = useDispatch<AppDispatch>();
  const [activationState, setActivationState] = useState<TrialActivationState>({
    showConfirmation: false,
    priceId: null,
    confirming: false,
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('trial_activated') !== 'true') {
      return;
    }

    const priceId = params.get('price_id') || null;

    // Clear the query params immediately to prevent duplicate processing
    window.history.replaceState({}, document.title, window.location.pathname);

    if (!priceId) {
      Notify.error(
        'Subscription setup incomplete. Please try activating your subscription again.'
      );
      return;
    }

    // Show confirmation modal instead of auto-activating
    setActivationState({
      showConfirmation: true,
      priceId,
      confirming: false,
    });
  }, []);

  const confirmActivation = async () => {
    if (!activationState.priceId) return;

    setActivationState((prev) => ({ ...prev, confirming: true }));

    const success = await activateTrialSubscription(activationState.priceId);
    if (success) {
      trackEvent('Trial Subscription Activated', {
        price_id: activationState.priceId,
      });
      Notify.success('Your subscription has been activated!');
      dispatch(getSubscriptionStatus());
      setActivationState({
        showConfirmation: false,
        priceId: null,
        confirming: false,
      });
    } else {
      trackEvent('Trial Subscription Activation Failed', {
        price_id: activationState.priceId,
      });
      Notify.error('Failed to activate subscription. Please try again.');
      // Keep the modal open so the user can retry
      setActivationState((prev) => ({ ...prev, confirming: false }));
    }
  };

  const cancelActivation = () => {
    setActivationState({
      showConfirmation: false,
      priceId: null,
      confirming: false,
    });
    Notify.info('Subscription activation cancelled');
  };

  return {
    activationState,
    confirmActivation,
    cancelActivation,
  };
}
